import React, { createContext, useContext, useState, useEffect } from 'react';
import safeStorage from '../utils/safeStorage';
import { STORAGE_KEYS } from '../utils/storageKeys';

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('AUD');
  const [exchangeRates] = useState({
    AUD: 1,
    USD: 0.65,
    EUR: 0.60,
    GBP: 0.52,
    CAD: 0.88
  });

  useEffect(() => {
    // Load currency preference from localStorage on mount
    const savedCurrency = safeStorage.getItem(STORAGE_KEYS.CURRENCY, 'AUD');
    if (savedCurrency && exchangeRates[savedCurrency]) {
      setCurrency(savedCurrency);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeCurrency = (newCurrency) => {
    if (exchangeRates[newCurrency]) {
      setCurrency(newCurrency);
      safeStorage.setItem(STORAGE_KEYS.CURRENCY, newCurrency);
    }
  };

  const convertPrice = (price, fromCurrency = 'AUD') => {
    const rate = exchangeRates[currency] / exchangeRates[fromCurrency];
    return price * rate;
  };

  const formatPrice = (price, fromCurrency = 'AUD') => {
    const symbols = {
      AUD: '$',
      USD: '$',
      EUR: '€',
      GBP: '£',
      CAD: 'C$'
    };
    
    // Locale settings based on currency (for proper number formatting)
    const localeMap = {
      AUD: 'en-AU',
      USD: 'en-US',
      EUR: 'en-EU',
      GBP: 'en-GB',
      CAD: 'en-CA'
    };
    
    const locale = localeMap[currency] || 'en-AU';
    
    // Handle price ranges (e.g., "14.50 - 16.50" or "14.50-16.50")
    if (typeof price === 'string' && (price.includes(' - ') || price.includes('-'))) {
      const priceParts = price.split(/\s*-\s*/);
      if (priceParts.length === 2) {
        const minPrice = parseFloat(priceParts[0]);
        const maxPrice = parseFloat(priceParts[1]);
        
        const convertedMin = convertPrice(minPrice, fromCurrency);
        const convertedMax = convertPrice(maxPrice, fromCurrency);
        
        // Format with thousand separators and conditional decimals
        const formatNumber = (num) => {
          const rounded = Math.round(num * 100) / 100; // Round to 2 decimal places
          if (rounded % 1 === 0) {
            // No cents - don't show .00
            return rounded.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
          } else {
            // Has cents - show decimals
            return rounded.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          }
        };
        
        return `${symbols[currency]}${formatNumber(convertedMin)} - ${symbols[currency]}${formatNumber(convertedMax)}`;
      }
    }
    
    // Handle single price
    const convertedPrice = convertPrice(price, fromCurrency);
    const rounded = Math.round(convertedPrice * 100) / 100; // Round to 2 decimal places
    
    // Format with thousand separators and conditional decimals
    let formattedNumber;
    if (rounded % 1 === 0) {
      // No cents - don't show .00
      formattedNumber = rounded.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    } else {
      // Has cents - show decimals
      formattedNumber = rounded.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    
    return `${symbols[currency]}${formattedNumber}`;
  };

  const value = {
    currency,
    exchangeRates,
    changeCurrency,
    convertPrice,
    formatPrice
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    console.warn('useCurrency used outside CurrencyProvider - returning default values');
    return {
      currency: 'AUD',
      exchangeRates: { AUD: 1 },
      changeCurrency: () => {},
      convertPrice: (price) => price,
      formatPrice: (price) => `$${price.toFixed(2)}`
    };
  }
  return context;
};
